// PowerSync client schema mirror.
//
// Declares the client-side SQLite schema that mirrors the server
// tables appearing in the four sync streams defined in
// `powersync/sync-config.yaml`. Only tables synced through
// PowerSync are listed here; `users` is intentionally excluded
// (display names are carried in JWT claims — syncing users would
// leak PII to the local SQLite mirror, per Phase B isolation decision).
//
// Column type mapping from Drizzle / Postgres → PowerSync client:
//   uuid / varchar / text / pgEnum / numeric / timestamptz → column.text
//   boolean                                                → column.integer
//   integer                                                → column.integer
//   real                                                   → column.real
//
// The `id` column is auto-created by PowerSync — do NOT declare it.
// See docs/plans/research/powersync-frontend-api.md §1 for the full
// mapping rationale.
//
// Stream assignments (from powersync/sync-config.yaml):
//   reference_global      — contracts, asset_systems, asset_sub_systems,
//                           asset_types, room_types, room_type_assets,
//                           building_types, building_type_room_types,
//                           floor_options
//   reference_site_scoped — bases, sites, buildings, floors, spaces
//   assignments           — user_site_assignments
//   captures              — capture_sessions, capture_items

import { column, Schema, Table } from '@powersync/web'

// ---------------------------------------------------------------------------
// reference_global stream
// ---------------------------------------------------------------------------

// contracts — top-level contract entity (~1–5 rows)
// Source: packages/shared/src/db/schema.ts :: contracts
const contracts = new Table({
  name: column.text,
  code: column.text,
  is_active: column.integer,   // boolean → integer
  created_at: column.text,     // timestamptz → text
  updated_at: column.text,
})

// asset_systems — CCL Asset Systems (~20–50 rows)
// Source: packages/shared/src/db/schema.ts :: assetSystems
const asset_systems = new Table({
  contract_id: column.text,    // uuid FK → text
  name: column.text,
  code: column.text,
  display_order: column.integer,
  rejected_at: column.text,    // timestamptz? → text (nullable maps to null)
  created_at: column.text,
  updated_at: column.text,
})

// asset_sub_systems — CCL Asset Sub Systems (~100–300 rows)
// Source: packages/shared/src/db/schema.ts :: assetSubSystems
const asset_sub_systems = new Table({
  system_id: column.text,
  name: column.text,
  code: column.text,
  display_order: column.integer,
  rejected_at: column.text,
  created_at: column.text,
  updated_at: column.text,
})

// asset_types — CCL Asset Types including field-added variants (~500–2 000 rows)
// Source: packages/shared/src/db/schema.ts :: assetTypes
const asset_types = new Table({
  sub_system_id: column.text,
  name: column.text,
  code: column.text,
  unit_of_measure: column.text,       // pgEnum → text
  grouping_convention: column.text,   // pgEnum → text
  is_field_added: column.integer,     // boolean → integer
  requires_review: column.integer,    // boolean → integer
  rejected_at: column.text,
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// room_types — room / space taxonomy (~50–200 rows)
// Source: packages/shared/src/db/schema.ts :: roomTypes
const room_types = new Table({
  contract_id: column.text,
  name: column.text,
  is_serialisable: column.integer,    // boolean → integer
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// room_type_assets — template assets per room type (~500–3 000 rows)
// Source: packages/shared/src/db/schema.ts :: roomTypeAssets
const room_type_assets = new Table({
  room_type_id: column.text,
  asset_type_id: column.text,
  expected_quantity: column.text,     // numeric → text (preserves precision)
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// building_types — building template definitions (~20–100 rows)
// Source: packages/shared/src/db/schema-templates.ts :: buildingTypes
const building_types = new Table({
  contract_id: column.text,
  name: column.text,
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// building_type_room_types — building-type ↔ room-type junction (~50–400 rows)
// Source: packages/shared/src/db/schema-templates.ts :: buildingTypeRoomTypes
const building_type_room_types = new Table({
  building_type_id: column.text,
  room_type_id: column.text,
  // nullable; a44f4d9 — the floor_option a room type seeds onto (null = Ground Floor).
  // Mirrors the server SELECT * so PowerSync can materialise the column.
  floor_option_id: column.text,
  display_order: column.integer,
  created_at: column.text,
})

// floor_options — contract-scoped admin-managed floor name picker (~5–30 rows)
// Source: packages/shared/src/db/schema-floor-options.ts :: floorOptions
const floor_options = new Table({
  contract_id: column.text,
  name: column.text,
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// ---------------------------------------------------------------------------
// reference_site_scoped stream
// ---------------------------------------------------------------------------

// bases — one per base (~5 rows for a fully-assigned user)
// Source: packages/shared/src/db/schema.ts :: bases
const bases = new Table({
  contract_id: column.text,
  name: column.text,
  code: column.text,
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// sites — directly filterable by assigned_sites claim (~1–17 per user)
// Source: packages/shared/src/db/schema.ts :: sites
const sites = new Table({
  base_id: column.text,
  name: column.text,
  code: column.text,
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// buildings — filtered via site_id (~5–500 per user)
// Source: packages/shared/src/db/schema.ts :: buildings
const buildings = new Table({
  site_id: column.text,
  name: column.text,
  floc: column.text,
  floor_area_m2: column.text,         // numeric → text
  status: column.text,                // pgEnum → text
  building_type_id: column.text,
  is_field_added: column.integer,     // boolean → integer
  requires_review: column.integer,    // boolean → integer
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// floors — filtered via building_id subquery (~20–2 000 per user)
// Source: packages/shared/src/db/schema.ts :: floors
const floors = new Table({
  building_id: column.text,
  name: column.text,
  display_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
})

// spaces — filtered via building_id subquery (~100–10 000 per user)
// Source: packages/shared/src/db/schema.ts :: spaces
//
// `created_in_session_id` is the per-row ownership signal used by
// cancel-session to scope orphan-room cleanup. Mirrored into the
// PowerSync local schema so the optimistic insert is byte-compatible
// with the server's authoritative row when it streams back. Codex P2
// on PR #548.
//
// `seeded_from_building_type_id` records which building type auto-seeded
// the room (provenance for the building-type → room feature). It ships
// via `SELECT * FROM spaces` and must be declared here too, or PowerSync
// silently drops it on every device. GH #610 (migration 0012).
const spaces = new Table({
  building_id: column.text,
  floor_id: column.text,
  room_type_id: column.text,
  serial_number: column.integer,
  display_name: column.text,
  alias: column.text,                 // nullable varchar → text
  is_pseudo: column.integer,          // boolean → integer
  is_field_added: column.integer,     // boolean → integer
  requires_review: column.integer,    // boolean → integer
  created_at: column.text,
  updated_at: column.text,
  created_in_session_id: column.text, // nullable uuid → text
  seeded_from_building_type_id: column.text, // nullable uuid → text
})

// ---------------------------------------------------------------------------
// assignments stream
// ---------------------------------------------------------------------------

// user_site_assignments — ~1–17 rows per field staff user
// Source: packages/shared/src/db/schema-assignments.ts :: userSiteAssignments
const user_site_assignments = new Table({
  user_id: column.text,
  site_id: column.text,
  created_by: column.text,
  created_at: column.text,
  updated_at: column.text,
})

// ---------------------------------------------------------------------------
// captures stream
// ---------------------------------------------------------------------------

// capture_sessions — the user's full session history (one bucket per user)
// Source: packages/shared/src/db/schema.ts :: captureSessions
//
// trackPrevious: { columns: ['completed_at', 'sync_version'] }
//
// `completed_at` is the discriminator between completeSession (was
// null → now non-null) and reopenSession (was non-null → now null) —
// both write to the same row with the same columns and the prior
// value of completed_at is the only signal of intent.
//
// `sync_version` carries the caller's PRE-BUMP version through to the
// connector. The adapter writes the optimistic post-bump value (N+1)
// to the local row so the dispatch result gives the caller a syncVersion
// that represents the eventual server state — the UI then drives its
// next toggle off that value without needing a round-trip. But the
// backend's `applyVersionGuard` validates against the caller's
// PRE-UPDATE version (N), not the optimistic N+1. Routing the
// pre-bump value through `previousValues` lets the connector send N
// to the backend while the local row keeps the optimistic N+1 for the
// UI. Codex P1 caught the missing pre-bump on the first cut of this
// path; without trackPrevious-on-sync_version every toggle 409'd on
// upload and the swallow path acked the failed batch, so completion
// never reached the backend.
//
// `onlyWhenChanged: true` skips the prev-value pair when the column
// wasn't touched.
const capture_sessions = new Table(
  {
    site_id: column.text,
    building_id: column.text,
    user_id: column.text,
    user_display_name: column.text,
    started_at: column.text,
    completed_at: column.text,
    source_device_id: column.text,
    synced_at: column.text,
    sync_version: column.integer,
    in_sync_window: column.integer, // boolean → integer; legacy window flag, unused
    created_at: column.text,
    updated_at: column.text,
  },
  {
    trackPrevious: {
      columns: ['completed_at', 'sync_version'],
      onlyWhenChanged: true,
    },
  },
)

// capture_items — the user's full history (one bucket per user)
// Source: packages/shared/src/db/schema-capture-items.ts :: captureItems (column is capture_session_id, not session_id)
//
// trackPrevious on sync_version: the connector's PATCH dispatch sends
// the PRE-bump version to the backend (the row version the user's edit
// applied on top of), while the adapter writes the OPTIMISTIC N+1 to
// local SQLite. Without trackPrevious, `previousValues['sync_version']`
// is empty and the connector has no way to recover N — it'd either send
// the bumped N+1 (rejected by applyVersionGuard with failed_precondition)
// or omit the field entirely (rejected by the request decoder with
// invalid_argument and a wedged upload queue). Same shape as the
// capture_sessions trackPrevious config above; see connector.ts and
// connector-dispatch-session.ts for the consumer ends.
// trackMetadata: durable `_metadata` FK snapshot (see capture-item-increment-sql.ts
// + connector-quarantine.ts) so a thin PATCH never leaves a context-less bare row.
const capture_items = new Table(
  {
    capture_session_id: column.text,
    user_id: column.text,               // denormalised owner (migration 0021) — the captures bucket key
    site_id: column.text,
    building_id: column.text,
    space_id: column.text,
    asset_type_id: column.text,
    quantity: column.text,              // numeric → text (preserves precision)
    is_zero_confirmed: column.integer,  // boolean → integer
    notes: column.text,
    captured_at: column.text,
    updated_at: column.text,
    sync_version: column.integer,
    rejected_at: column.text,           // timestamptz? → text (soft-delete marker)
  },
  {
    trackPrevious: {
      columns: ['sync_version'],
      onlyWhenChanged: true,
    },
    trackMetadata: true,
  },
)

// ---------------------------------------------------------------------------
// Local-only tables (never synced, never generate upload CRUD entries)
// ---------------------------------------------------------------------------

// pending_space_deletes — user-intent delete tombstones (#596 / ADR-015).
//
// This table exists ONLY on the device. A PowerSync `{ localOnly: true }`
// table never syncs upstream and never produces an upload CRUD entry of its
// own, but it persists in local SQLite across app restarts — exactly the
// durability the offline-delete path needs.
//
// Why a tombstone at all: when the surveyor deletes a room offline, the
// adapter (capture-client-powersync-delete-space.ts) issues
// `DELETE FROM spaces WHERE id=?`. The row is then GONE from the local
// mirror, and the spaces-DELETE CRUD entry the SDK queues carries only
// `entry.id` — no row data. The connector can't tell that delete apart from
// the re-mint orphan-drop (connector-dispatch-space.ts → remintSpaceUnderNewId
// issues a byte-identical `DELETE FROM spaces`). The user-intent delete must
// PROPAGATE to the server (DELETE /capture/sessions/:id/spaces/:spaceId); the
// orphan-drop must NOT (the server never accepted that id). The tombstone is
// the discriminator: the dispatch writes one row keyed by `space_id` BEFORE
// it deletes the spaces row; the connector reads it back AFTER the row is gone
// to (a) recognise the delete as user-intent and (b) recover the `session_id`
// the gone row can't carry. The connector deletes the tombstone once the
// server confirms (2xx) or reports the room already gone (404 — idempotent);
// on a genuine 5xx it leaves both the queued DELETE and the tombstone in place
// so the next upload cycle retries (idempotently).
//
// No id/created_at/updated_at: PowerSync auto-creates the `id` column; this
// table is a transient work-queue, not an LWW-synced entity, so it needs no
// timestamps and no trigger (the lint:updated-at-triggers gate only scans
// Drizzle pgTable definitions in packages/shared, not this client mirror).
const pending_space_deletes = new Table(
  {
    space_id: column.text,
    session_id: column.text,
  },
  { localOnly: true },
)

// orphaned_capture_items — quarantine for counts that can never sync.
//
// Local-only (like pending_space_deletes above): never synced upstream, never
// generates an upload CRUD entry, persists across restarts. When a
// capture_items create/increment can NEVER land on the server — its FK parent
// (space/session) is absent so the backend 404s — and the count could not be
// re-homed onto an authoritative row, the connector copies the FULL payload
// HERE before removing the un-syncable row from capture_items to drain the
// upload queue. Without this the count, which lived ONLY on this device, was
// silently DELETEd (contractual data-loss — AGENTS.md "Business criticality").
// `id` is the original capture_items id (one orphan → one quarantine row) so
// re-quarantine is idempotent. A recovery surface (use-quarantined-captures.ts)
// reads this table reactively so the surveyor can see and re-inject held counts.
const orphaned_capture_items = new Table(
  {
    original_item_id: column.text,
    capture_session_id: column.text,
    site_id: column.text,
    building_id: column.text,
    space_id: column.text,
    asset_type_id: column.text,
    quantity: column.text,
    is_zero_confirmed: column.integer,
    notes: column.text,
    captured_at: column.text,
    reason: column.text,
    orphaned_at: column.text,
  },
  { localOnly: true },
)

// session_rehomes — durable orphan→authoritative capture_session mapping.
//
// Local-only (never synced). When a createSession PUT collides (409
// already_exists) because the user already had an active server session for the
// (user, building) that hadn't synced down, the connector re-homes the orphan's
// children onto the authoritative session. A complete/reopen the surveyor tapped
// OFFLINE was queued as a PATCH against the ORPHAN id; it must redirect onto the
// authoritative id (and at the authoritative syncVersion) instead of 404-ing.
// This was a module-memory Map, so a mobile reload/background-kill between the
// INSERT batch and the queued-complete batch LOST the mapping → the complete
// targeted the deleted orphan, 404'd, was acked, and the building silently
// stayed in the wrong state (Codex on PR 660). Persisting it here survives the
// reload. `id` is the orphan (client-minted) session id, PK (one orphan → one
// re-home), so re-record is idempotent.
const session_rehomes = new Table(
  {
    authoritative_id: column.text,
    authoritative_sync_version: column.integer,
    recorded_at: column.text,
  },
  { localOnly: true },
)

// floor_origin_sessions — per-floor origin-session stamp (C2/#690 + H1/#693).
//
// Local-only (like the three tables above): never synced, never generates an
// upload CRUD entry, persists across restarts. `id` is the device-minted floor
// id; `origin_session_id` is the capture session the floor was resolved in
// (resolve-floor-local.ts writes it on the lazy CREATE only). The connector's
// floors PUT dispatch (connector-dispatch-floor.ts) reads it back to tell a
// CANCELLED-origin floor — origin session hard-deleted by cancelSession, so the
// queued PUT can never land — from a TRANSIENT cross-batch race where the
// session simply isn't resolvable yet, exactly as the spaces leg uses the
// SYNCED `spaces.created_in_session_id` column (#669). Floors is admin-curated
// reference data in the reference_site_scoped stream, so it carries no
// session column and adding one would mean a server migration + sync-rule
// change; a device-local stamp is sufficient because the signal is only ever
// needed on THIS device at dispatch time (a floor that already synced never
// re-enters the PUT dispatch path). `id` is the PK so a re-stamp is idempotent.
const floor_origin_sessions = new Table(
  {
    origin_session_id: column.text,
  },
  { localOnly: true },
)

// ---------------------------------------------------------------------------
// Schema export
// ---------------------------------------------------------------------------

export const AppSchema = new Schema({
  // reference_global
  contracts,
  asset_systems,
  asset_sub_systems,
  asset_types,
  room_types,
  room_type_assets,
  building_types,
  building_type_room_types,
  floor_options,
  // reference_site_scoped
  bases,
  sites,
  buildings,
  floors,
  spaces,
  // assignments
  user_site_assignments,
  // captures
  capture_sessions,
  capture_items,
  // local-only (never synced)
  pending_space_deletes,
  orphaned_capture_items,
  session_rehomes,
  floor_origin_sessions,
})

// Per-table row types derived from the schema — available for
// typed useQuery() results in Phase D-2 and beyond.
export type Database = (typeof AppSchema)['types']
export type ContractRecord = Database['contracts']
export type AssetSystemRecord = Database['asset_systems']
export type AssetSubSystemRecord = Database['asset_sub_systems']
export type AssetTypeRecord = Database['asset_types']
export type RoomTypeRecord = Database['room_types']
export type RoomTypeAssetRecord = Database['room_type_assets']
export type BuildingTypeRecord = Database['building_types']
export type BuildingTypeRoomTypeRecord = Database['building_type_room_types']
export type FloorOptionRecord = Database['floor_options']
export type BaseRecord = Database['bases']
export type SiteRecord = Database['sites']
export type BuildingRecord = Database['buildings']
export type FloorRecord = Database['floors']
export type SpaceRecord = Database['spaces']
export type UserSiteAssignmentRecord = Database['user_site_assignments']
export type CaptureSessionRecord = Database['capture_sessions']
export type CaptureItemRecord = Database['capture_items']
export type PendingSpaceDeleteRecord = Database['pending_space_deletes']
export type OrphanedCaptureItemRecord = Database['orphaned_capture_items']
export type SessionRehomeRecord = Database['session_rehomes']
export type FloorOriginSessionRecord = Database['floor_origin_sessions']
